import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { TagsIcon } from 'lucide-react';
import { toast } from 'sonner';

import { useCustomerCtx } from '@/app/(app)/customers/[id]/provider';
import { Select } from '@/components/atoms/select';
import { useFeatureFlag } from '@/components/providers/feature-flag-provider';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

export function CustomerServicePlans() {
  return (
    <div className='flex flex-col gap-6'>
      <div className='flex items-center justify-between'>
        <div className='text-lg font-semibold'>Subscriptions</div>
        <AddServicePlanDialog />
      </div>
      <ServicePlanList />
    </div>
  );
}

function AddServicePlanDialog() {
  const isEnabled = useFeatureFlag('/customers/service_plan/create');
  const { api } = useApiV1Ctx();
  const { customer, refetch } = useCustomerCtx();
  const [open, setOpen] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const { data: templates } = api.servicePlanTemplates.getAllServicePlanTemplatesGet.useQuery(undefined, {
    enabled: open,
  });
  const options = useMemo(() => {
    return templates?.map((template) => ({
      label: template.name,
      value: template.id,
    }));
  }, [templates]);

  const mutation = api.servicePlans.createFromTemplateServicePlansCreateFromTemplatePost.useMutation(
    {},
    {
      onSuccess: () => {
        refetch();
        setOpen(false);
      },
    }
  );

  const handleSubmit = () => {
    if (!templateId) {
      toast.error('Please select a subscription');
      return;
    }

    mutation.mutate({
      template_id: templateId,
      customer_id: customer.id,
      overrides: {},
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild disabled={!isEnabled}>
        <Button size='sm'>Add Service Plan</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add subscription for customer</DialogTitle>
          <DialogDescription>Select a subscription to add for this customer</DialogDescription>
        </DialogHeader>
        <div className='flex w-full flex-col gap-6'>
          <div className='flex flex-col gap-1.5'>
            <Label>Subscription</Label>
            <Select
              options={options ?? []}
              value={templateId ?? ''}
              onChange={(value) => setTemplateId(value)}
              placeholder='Select a subscription'
              className='w-full'
            />
          </div>
        </div>
        <DialogFooter className='mt-6'>
          <Button onClick={handleSubmit} disabled={mutation.isPending || !templateId}>
            {mutation.isPending ? 'Adding...' : 'Add Subscription'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ServicePlanList() {
  const { customer } = useCustomerCtx();
  const plans = useMemo(() => {
    return customer.service_plans.sort((a, b) => {
      if (a.cancelled_at && !b.cancelled_at) return 1;
      if (!a.cancelled_at && b.cancelled_at) return -1;
      return a.name.localeCompare(b.name);
    });
  }, [customer.service_plans]);

  if (customer.service_plans.length === 0) {
    return (
      <div className='w-full rounded-md border border-dashed border-muted-foreground/50 p-8 text-center'>
        <p className='text-sm text-muted-foreground'>No subscriptions</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      {plans.map((plan) => (
        <ServicePlanCard key={plan.id} plan={plan} />
      ))}
    </div>
  );
}

interface ServicePlanCardProps {
  plan: ApiSchemas['CustomerReadComplete']['service_plans'][number];
}

function ServicePlanCard({ plan: servicePlan }: ServicePlanCardProps) {
  const { customer } = useCustomerCtx();
  const serviceTypes = useMemo(() => {
    return customer.service_types.filter((serviceType) => serviceType.service_plan_id === servicePlan.id);
  }, [customer.service_types, servicePlan.id]);

  return (
    <div className='flex w-full flex-col gap-4 rounded-4xl p-4 shadow-md'>
      <div className='flex items-center gap-3'>
        <TagsIcon className='size-4' />
        <h3 className='text-base font-medium'>{servicePlan.name}</h3>
        {servicePlan.cancelled_at && (
          <Badge variant='secondary' className='opacity-50'>
            Cancelled
          </Badge>
        )}
        {!servicePlan.cancelled_at && <Badge variant='secondary'>Active</Badge>}
      </div>
      <div className='border-t-[0.5px]'>
        <div className='flex items-center gap-2 py-2 not-last:border-b-[0.5px]'>
          <div className='w-40 text-sm text-muted-foreground'>Services</div>
          <div className='text-sm'>{serviceTypes.map((serviceType) => serviceType.name).join(', ')}</div>
        </div>
        {servicePlan.description && (
          <div className='flex items-center gap-2 py-2 not-last:border-b-[0.5px]'>
            <div className='w-40 text-sm text-muted-foreground'>Description</div>
            <div className='text-sm'>{servicePlan.description}</div>
          </div>
        )}
        <div className='flex items-center gap-2 py-2 not-last:border-b-[0.5px]'>
          <div className='w-40 text-sm text-muted-foreground'>Start Date</div>
          <div className='text-sm'>{format(servicePlan.start_date, 'MMM d, yyyy')}</div>
        </div>
        {servicePlan.cancelled_at && (
          <div className='flex items-center gap-2 py-2 not-last:border-b-[0.5px]'>
            <div className='w-40 text-sm text-muted-foreground'>Cancelled Date</div>
            <div className='text-sm'>{format(servicePlan.cancelled_at, 'MMM d, yyyy')}</div>
          </div>
        )}
      </div>
    </div>
  );
}
